/**
 * Co se ukáže, když plocha není k dispozici.
 *
 * Existuje proto, že dřív se v téhle situaci **tiše dosadil výchozí layout z kódu**. Ten se
 * ale s databází dávno rozešel, takže uživatel viděl jiný dashboard než ten, který si admin
 * nastavil — a nic tomu nenasvědčovalo (`docs/plans/DASHBOARD-A-KAMERY.md:31-34`).
 * Poctivá hláška je horší na pohled a lepší k životu.
 */

import React from "react";
import { AlertTriangle, LogIn, RefreshCw } from "lucide-react";
import { t } from "@client/lib/dictionary";
import { Button } from "@client/components/ui/button";
import { Spinner } from "@client/components/ui/spinner";
import { useLayoutStore, type LayoutStatus } from "@client/store/layout";
import { useUserStore } from "@client/store/user";

export interface LayoutUnavailableProps {
  layoutKey: string;
  status: LayoutStatus;
  /** Řádek v DB je, ale jeho obsah neprošel validací. */
  invalid?: boolean;
}

const Ramecek: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mx-auto my-8 max-w-lg rounded-lg border border-border bg-muted/30 p-6 text-center">{children}</div>
);

export const LayoutUnavailable: React.FC<LayoutUnavailableProps> = ({ layoutKey, status, invalid }) => {
  const load = useLayoutStore((s) => s.load);
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";

  if (status === "idle" || status === "loading") {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (status === "unauthorized") {
    return (
      <Ramecek>
        <LogIn className="mx-auto mb-2 size-6 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">{t.layout.unauthorized}</p>
      </Ramecek>
    );
  }

  if (status === "error") {
    return (
      <Ramecek>
        <AlertTriangle className="mx-auto mb-2 size-6 text-status-warning" aria-hidden />
        <p className="mb-3 text-sm">{t.layout.loadFailed}</p>
        <Button size="sm" variant="outline" onClick={() => void load()}>
          <RefreshCw className="mr-1 size-3.5" />
          {t.app.retry}
        </Button>
      </Ramecek>
    );
  }

  // `ready`, ale plocha chybí nebo je poškozená — obojí znamená problém na serveru,
  // ne na spojení, takže „Zkusit znovu" by nepomohlo.
  return (
    <Ramecek>
      <AlertTriangle className="mx-auto mb-2 size-6 text-status-serious" aria-hidden />
      <p className="text-sm">{invalid ? t.layout.corrupted : t.layout.missing(layoutKey)}</p>
      {isAdmin && <p className="mt-2 text-xs text-muted-foreground">{invalid ? t.layout.corruptedHint : t.layout.missingHint}</p>}
    </Ramecek>
  );
};
